'use client';

import { createContext, useContext, useState, ReactNode, useCallback } from 'react';

import Modal, { ModalContextType, ModalState, ModalConfig, ModalID } from './Modal';

const ModalContext = createContext<ModalContextType | undefined>(undefined);

const defaultModalConfig: ModalConfig = {
  size: 'md',
  position: 'center',
  showCloseButton: true,
  closeOnBackdropClick: true,
  closeOnEscape: true,
  animation: 'fade',
  zIndex: 50,
  persistent: false,
};

interface ModalProviderProps {
  children?: ReactNode;
}

export function ModalProvider({ children }: ModalProviderProps) {
  const [modals, setModals] = useState<Record<string, ModalState>>({});

  const openModal = useCallback((id: ModalID, config: Partial<ModalConfig> = {}) => {
    setModals(prev => ({
      ...prev,
      [id]: {
        isOpen: true,
        config: { ...defaultModalConfig, ...config },
      },
    }));
  }, []);

  const closeModal = useCallback((id: ModalID) => {
    setModals(prev => {
      const newModals = { ...prev };
      if (newModals[id]) {
        newModals[id] = {
          ...newModals[id],
          isOpen: false,
        };
      }
      return newModals;
    });

    // Remove modal from state after animation completes
    setTimeout(() => {
      setModals(prev => {
        const newModals = { ...prev };
        delete newModals[id];
        return newModals;
      });
    }, 300);
  }, []);

  const closeAllModals = useCallback(() => {
    setModals(prev => {
      const newModals = { ...prev };
      Object.keys(newModals).forEach(id => {
        if (!newModals[id].config.persistent) {
          newModals[id] = {
            ...newModals[id],
            isOpen: false,
          };
        }
      });
      return newModals;
    });

    // Remove non-persistent modals after animation completes
    setTimeout(() => {
      setModals(prev => {
        const newModals: Record<string, ModalState> = {};
        Object.entries(prev).forEach(([id, modal]) => {
          if (modal.config.persistent) {
            newModals[id] = modal;
          }
        });
        return newModals;
      });
    }, 300);
  }, []);

  const isModalOpen = useCallback(
    (id: ModalID) => {
      return modals[id]?.isOpen || false;
    },
    [modals]
  );

  const updateModal = useCallback((id: ModalID, config: Partial<ModalConfig>) => {
    setModals(prev => {
      if (!prev[id]) return prev;

      return {
        ...prev,
        [id]: {
          ...prev[id],
          config: { ...prev[id].config, ...config },
        },
      };
    });
  }, []);

  const value: ModalContextType = {
    modals,
    openModal,
    closeModal,
    closeAllModals,
    isModalOpen,
    updateModal,
  };

  return (
    <ModalContext.Provider value={value}>
      {children}
      {/* Render all active modals */}
      {Object.entries(modals).map(([id, modalState]) => (
        <ModalRenderer key={id} id={id} modalState={modalState} onClose={() => closeModal(id)} />
      ))}
    </ModalContext.Provider>
  );
}

// Modal renderer component
interface ModalRendererProps {
  id: string;
  modalState: ModalState;
  onClose: () => void;
}

function ModalRenderer({ id, modalState, onClose }: ModalRendererProps) {
  const { isOpen, config } = modalState;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={config.title}
      subtitle={config.subtitle}
      size={config.size}
      position={config.position}
      showCloseButton={config.showCloseButton}
      closeOnBackdropClick={config.closeOnBackdropClick}
      closeOnEscape={config.closeOnEscape}
      className={config.className}
      overlayClassName={config.overlayClassName}
      contentClassName={config.contentClassName}
      animation={config.animation}
      zIndex={config.zIndex}
    >
      {/* Content will be provided by components using the modal */}
      <div data-modal-id={id}>{/* This is where the modal content component will render */}</div>
    </Modal>
  );
}

export function useModal() {
  const context = useContext(ModalContext);
  if (context === undefined) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
}

// Convenience hooks for common modal types
export function useConfirmModal() {
  const { openModal, closeModal } = useModal();

  const confirm = useCallback(
    (title: string, message: string, onConfirm: () => void, onCancel?: () => void) => {
      const modalId = 'confirm-modal';
      openModal(modalId, {
        title,
        subtitle: message,
        size: 'sm',
        closeOnBackdropClick: false,
        closeOnEscape: false,
      });

      // You would implement the confirm dialog content separately
      // This is just the hook to manage the modal state
    },
    [openModal]
  );

  return { confirm };
}

export function useNotificationModal() {
  const { openModal, closeModal } = useModal();

  const notify = useCallback(
    (title: string, message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info') => {
      const modalId = `notification-${Date.now()}`;
      openModal(modalId, {
        title,
        subtitle: message,
        size: 'sm',
        position: 'top',
        animation: 'slide-down',
      });

      // Auto-close after 3 seconds
      setTimeout(() => {
        closeModal(modalId);
      }, 3000);
    },
    [openModal, closeModal]
  );

  return { notify };
}
